import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Edit, Trash2, UserPlus, Eye, EyeOff } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useDataCache } from '@/hooks/useDataCache';

interface User {
  id: number;
  fullName: string;
  email: string;
  username: string;
  roleId: number;
  roleName: string;
  emailVerified: boolean;
  isActive: boolean;
  createdAt: string;
}

export default function Users() {
  const { t } = useTranslation();
  const { getUsersData, getRolesData, cachedData, invalidateCache } = useDataCache();
  const editFormFirstFieldRef = useRef<HTMLInputElement>(null);
  const createFormRef = useRef<HTMLDivElement>(null);
  const createFormFirstFieldRef = useRef<HTMLInputElement>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<number | 'all' | 'all_except_students'>('all_except_students');
  const [showPassword, setShowPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use cached data instead of local state
  const users = cachedData.users || [];
  const roles = cachedData.roles || [];
  
  const [newUser, setNewUser] = useState({
    fullName: '',
    email: '',
    username: '',
    password: '',
    roleId: 1,
    isActive: true
  });

  const [editUser, setEditUser] = useState({
    fullName: '',
    email: '',
    username: '',
    password: '',
    roleId: 1,
    isActive: true
  });

  // Load users and roles from cache
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load both users and roles from cache
      await Promise.all([
        getUsersData(),
        getRolesData()
      ]);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error al cargar datos');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Update default role when roles are loaded
  useEffect(() => {
    if (roles.length > 0 && newUser.roleId === 1) {
      setNewUser(prev => ({ ...prev, roleId: roles[0].id }));
    }
  }, [roles]);

  // Find Alumno role ID for filtering
  const alumnoRole = roles.find(role => 
    role.name.toLowerCase() === 'alumno' || 
    role.name.toLowerCase() === 'student' ||
    role.name.toLowerCase() === 'alumnos'
  );

  // Auto-scroll and focus on the first field when create form opens
  useEffect(() => {
    if (showCreateForm && createFormRef.current) {
      setTimeout(() => {
        createFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        createFormFirstFieldRef.current?.focus();
      }, 100);
    }
  }, [showCreateForm]);

  // Auto-focus on the first field when edit form opens
  useEffect(() => {
    if (showEditForm && editFormFirstFieldRef.current) {
      setTimeout(() => {
        editFormFirstFieldRef.current?.focus();
        editFormFirstFieldRef.current?.select();
      }, 100);
    }
  }, [showEditForm]);

  // Filter users based on search term and role filter
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.roleName.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesRole = false;
    if (roleFilter === 'all') {
      matchesRole = true;
    } else if (roleFilter === 'all_except_students') {
      // Exclude students/alumnos when "all except students" is selected
      matchesRole = !alumnoRole || user.roleId !== alumnoRole.id;
    } else {
      matchesRole = user.roleId === roleFilter;
    }
    
    return matchesSearch && matchesRole;
  });

  // Create user function
  const handleCreateUser = async () => {
    if (!newUser.fullName || !newUser.email || !newUser.username || !newUser.password) {
      alert('Por favor complete todos los campos requeridos');
      return;
    }

    // Password validation
    if (newUser.password.length < 8) {
      alert('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
    if (!passwordRegex.test(newUser.password)) {
      alert('La contraseña debe tener al menos 1 mayúscula, 1 minúscula y 1 dígito');
      return;
    }

    try {
      setCreateLoading(true);
      setError(null);

      const response = await fetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newUser),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear usuario');
      }

      const createdUser = await response.json();

      // Invalidate cache and force immediate reload
      invalidateCache('users');
      await getUsersData(); // Force immediate refresh

      // Reset form
      setNewUser({
        fullName: '',
        email: '',
        username: '',
        password: '',
        roleId: roles.length > 0 ? roles[0].id : 1,
        isActive: true
      });
      setShowCreateForm(false);

      alert(`Usuario creado exitosamente. El usuario puede iniciar sesión con:\nUsuario: ${createdUser.username}\nContraseña: [la que definiste]`);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error desconocido');
      alert(error instanceof Error ? error.message : 'Error al crear usuario');
    } finally {
      setCreateLoading(false);
    }
  };

  // Delete user function
  const handleDeleteUser = async (userId: number) => {
    if (!confirm('¿Estás seguro de que deseas desactivar este usuario?')) {
      return;
    }

    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar usuario');
      }

      // Invalidate cache and force immediate reload
      invalidateCache('users');
      await getUsersData(); // Force immediate refresh
      alert('Usuario desactivado exitosamente');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Error al eliminar usuario');
    }
  };

  // Update user function
  const handleUpdateUser = async () => {
    if (!editingUser) return;

    if (!editUser.fullName || !editUser.email || !editUser.username) {
      alert('Por favor complete todos los campos requeridos');
      return;
    }

    // Password validation (only if password is provided)
    if (editUser.password) {
      if (editUser.password.length < 8) {
        alert('La contraseña debe tener al menos 8 caracteres');
        return;
      }

      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
      if (!passwordRegex.test(editUser.password)) {
        alert('La contraseña debe tener al menos 1 mayúscula, 1 minúscula y 1 dígito');
        return;
      }
    }

    try {
      setEditLoading(true);
      setError(null);

      const updateData: any = {
        fullName: editUser.fullName,
        email: editUser.email,
        username: editUser.username,
        roleId: editUser.roleId,
        isActive: editUser.isActive,
      };

      // Only include password if it's provided
      if (editUser.password) {
        updateData.password = editUser.password;
      }

      const response = await fetch(`/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar usuario');
      }

      // Get updated user data from response
      const updatedUser = await response.json();

      // Invalidate cache and force immediate reload
      invalidateCache('users');
      await getUsersData(); // Force immediate refresh

      // Update the form with the fresh data (excluding password)
      setEditUser({
        fullName: updatedUser.fullName,
        email: updatedUser.email,
        username: updatedUser.username,
        password: '', // Always clear password field for security
        roleId: updatedUser.roleId,
        isActive: updatedUser.isActive
      });

      // Update the editing user reference
      setEditingUser(updatedUser);

      alert('Usuario actualizado exitosamente');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error desconocido');
      alert(error instanceof Error ? error.message : 'Error al actualizar usuario');
    } finally {
      setEditLoading(false);
    }
  };

  const openEditForm = (user: User) => {
    setEditingUser(user);
    setEditUser({
      fullName: user.fullName,
      email: user.email,
      username: user.username,
      password: '', // Don't populate password
      roleId: user.roleId,
      isActive: user.isActive
    });
    setShowEditForm(true);
  };

  const handleToggleActive = async (user: User) => {
    try {
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          isActive: !user.isActive,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar usuario');
      }

      // Invalidate cache and force immediate reload
      invalidateCache('users');
      await getUsersData(); // Force immediate refresh
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Error al actualizar usuario');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t('app.users')}</h2>
          <p className="text-muted-foreground">
            Gestiona usuarios, roles y permisos del sistema.
          </p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">Cargando usuarios...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t('app.users')}</h2>
          <p className="text-muted-foreground">
            Gestiona usuarios, roles y permisos del sistema.
          </p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-red-600">
              <p>{error}</p>
              <Button onClick={loadData} className="mt-4">
                Reintentar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t('app.users')}</h2>
          <p className="text-muted-foreground">
            Gestiona usuarios, roles y permisos del sistema.
          </p>
        </div>
        <Button onClick={() => setShowCreateForm(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Crear Usuario
        </Button>
      </div>

      {/* Search bar and filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder={`${t('common.search')} usuarios...`}
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <select
                className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 dark:border-gray-600"
                value={roleFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'all' || val === 'all_except_students') {
                    setRoleFilter(val);
                  } else {
                    setRoleFilter(parseInt(val));
                  }
                }}
              >
                <option value="all_except_students">Colaboradores (sin Alumnos)</option>
                <option value="all">Todos los roles</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit User Form */}
      {showEditForm && editingUser && (
        <Card>
          <CardHeader>
            <CardTitle>Editar Usuario</CardTitle>
            <CardDescription>Actualizar información de {editingUser.fullName}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nombre Completo *</label>
                <Input
                  ref={editFormFirstFieldRef}
                  value={editUser.fullName}
                  onChange={(e) => setEditUser(prev => ({ ...prev, fullName: e.target.value }))}
                  placeholder="Juan Pérez López"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <Input
                  type="email"
                  value={editUser.email}
                  onChange={(e) => setEditUser(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="juan.perez@empresa.com"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nombre de Usuario *</label>
                <Input
                  value={editUser.username}
                  onChange={(e) => setEditUser(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="jperez"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Nueva Contraseña (opcional)</label>
                <div className="relative">
                  <Input
                    type={showEditPassword ? 'text' : 'password'}
                    value={editUser.password}
                    onChange={(e) => setEditUser(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Dejar vacío para mantener contraseña actual"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEditPassword(!showEditPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showEditPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Solo complete si desea cambiar la contraseña
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Rol *</label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={editUser.roleId}
                onChange={(e) => setEditUser(prev => ({ ...prev, roleId: parseInt(e.target.value) }))}
              >
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="editIsActive"
                checked={editUser.isActive}
                onChange={(e) => setEditUser(prev => ({ ...prev, isActive: e.target.checked }))}
              />
              <label htmlFor="editIsActive" className="text-sm">Usuario activo</label>
            </div>

            <div className="flex space-x-2">
              <Button onClick={handleUpdateUser} disabled={editLoading}>
                {editLoading ? 'Actualizando...' : 'Actualizar Usuario'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEditForm(false);
                  setEditingUser(null);
                  setEditUser({
                    fullName: '',
                    email: '',
                    username: '',
                    password: '',
                    roleId: roles.length > 0 ? roles[0].id : 1,
                    isActive: true
                  });
                }}
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create User Form */}
      {showCreateForm && (
        <Card ref={createFormRef}>
          <CardHeader>
            <CardTitle>Crear Nuevo Usuario</CardTitle>
            <CardDescription>Agregue los datos del nuevo usuario del sistema</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nombre Completo *</label>
                <Input
                  ref={createFormFirstFieldRef}
                  value={newUser.fullName}
                  onChange={(e) => setNewUser(prev => ({ ...prev, fullName: e.target.value }))}
                  placeholder="Juan Pérez López"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="juan.perez@empresa.com"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nombre de Usuario *</label>
                <Input
                  value={newUser.username}
                  onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="jperez"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Contraseña *</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newUser.password}
                    onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Mínimo 8 caracteres, 1 mayúscula, 1 minúscula, 1 dígito"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  La contraseña debe tener al menos 8 caracteres con 1 mayúscula, 1 minúscula y 1 número
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Rol *</label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={newUser.roleId}
                onChange={(e) => setNewUser(prev => ({ ...prev, roleId: parseInt(e.target.value) }))}
              >
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isActive"
                checked={newUser.isActive}
                onChange={(e) => setNewUser(prev => ({ ...prev, isActive: e.target.checked }))}
              />
              <label htmlFor="isActive" className="text-sm">Usuario activo</label>
            </div>

            <div className="flex space-x-2">
              <Button onClick={handleCreateUser} disabled={createLoading}>
                {createLoading ? 'Creando...' : 'Crear Usuario'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCreateForm(false);
                  setNewUser({
                    fullName: '',
                    email: '',
                    username: '',
                    password: '',
                    roleId: roles.length > 0 ? roles[0].id : 1,
                    isActive: true
                  });
                }}
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users table */}
      <Card>
        <CardHeader>
          <CardTitle>Usuarios del Sistema</CardTitle>
          <CardDescription>Lista de usuarios registrados y sus roles</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="w-full">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium">Usuario</th>
                  <th className="text-left py-3 px-4 font-medium">Email</th>
                  <th className="text-left py-3 px-4 font-medium">Nombre de Usuario</th>
                  <th className="text-left py-3 px-4 font-medium">Rol</th>
                  <th className="text-left py-3 px-4 font-medium">Estado</th>
                  <th className="text-left py-3 px-4 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      {searchTerm ? 'No se encontraron usuarios que coincidan con la búsqueda' : 'No hay usuarios registrados'}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium">{user.fullName}</div>
                          <div className="text-sm text-gray-500">ID: {user.id}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>{user.email}</div>
                        {user.emailVerified ? (
                          <span className="inline-block w-2 h-2 bg-green-400 rounded-full mr-1"></span>
                        ) : (
                          <span className="inline-block w-2 h-2 bg-yellow-400 rounded-full mr-1"></span>
                        )}
                        <span className="text-xs text-gray-500">
                          {user.emailVerified ? 'Verificado' : 'Pendiente'}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono">{user.username}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          {user.roleName}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleToggleActive(user)}
                          className={`px-2 py-1 text-xs rounded-full ${
                            user.isActive 
                              ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          {user.isActive ? 'Activo' : 'Inactivo'}
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openEditForm(user)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{users.length}</div>
            <p className="text-xs text-muted-foreground">Total usuarios</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{users.filter(u => u.isActive).length}</div>
            <p className="text-xs text-muted-foreground">Usuarios activos</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{users.filter(u => u.emailVerified).length}</div>
            <p className="text-xs text-muted-foreground">Emails verificados</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{roles.length}</div>
            <p className="text-xs text-muted-foreground">Roles disponibles</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}